import type { ReactNode } from 'react';
import { styled } from '@mui/material';
import SuccessIcon from '../icons/assets/correct.svg?react';

export type StepperNavigationItem = {
    label: ReactNode;
    active?: boolean;
    completed?: boolean;
};

// todo Better to name this "color"
type Variant = 'primary' | 'secondary';

const StepperNavigationListItem = styled('li', {
    name: 'StepperNavigation',
    slot: 'item',
    skipVariantsResolver: true,
    shouldForwardProp: (propName) =>
        !['active', 'sx', 'variant', 'completed'].includes(propName as string),
})<{ active?: boolean; variant: Variant; completed?: boolean }>(
    ({ theme, active, variant, completed }) => ({
        display: 'block',
        width: '100%',
        fontWeight: 'bold',
        paddingTop: 32 + 18,
        paddingLeft: 12,
        paddingRight: 12,
        paddingBottom: 0,
        boxSizing: 'border-box',
        position: 'relative',

        '& i': {
            fontSize: 19,
            lineHeight: '32px',
            boxSizing: 'border-box',
            position: `absolute`,
            top: 0,
            left: '50%',
            marginLeft: -16,
            // content: completed
            //     ? `url(data:image/svg+xml;utf8,${encodeURIComponent(successCircleIcon)})`
            //     : 'attr(data-content)',
            display: `inline-block`,
            width: 32,
            height: 32,
            zIndex: 1,
            borderRadius: `50%`,
            backgroundColor: theme.palette.common.white,
            border: `solid 1px ${theme.bino.color.lightGray}`,
            ...((active || completed) && {
                color: theme.bino.color.white,
                border: 'none',
            }),
            ...((active || completed) &&
                variant === 'primary' && {
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                }),
            ...((active || completed) &&
                variant === 'secondary' && {
                    backgroundColor: theme.bino.color.darkGreen,
                    borderColor: theme.bino.color.darkGreen,
                }),
        },

        '&:after': {
            content: '""',
            position: 'absolute',
            top: 14.5,
            right: 'calc(100% / 2)',
            width: '100%',
            height: 1,
            border: `solid 0px transparent`,
            borderBottomWidth: 2,
            borderColor: theme.bino.color.lightGray,
            zIndex: 0,
            ...(completed &&
                variant === 'secondary' && {
                    borderColor: theme.bino.color.darkGreen,
                }),
        },
        '&:first-of-type:after': {
            width: 0,
        },

        // '&:last-child': {
        //     '&:after': {
        //         width: 0,
        //     },
        // },
        ...(variant === 'primary' && {
            color: theme.palette.primary.main,
        }),
        color:
            variant === 'primary'
                ? theme.palette.primary.main
                : active
                  ? theme.bino.color.black
                  : theme.bino.color.label,

        [theme.breakpoints.down('sm')]: {
            display: 'block',
            textAlign: 'left',
            paddingTop: 15,
            paddingLeft: 40,
            paddingBottom: 20,
            lineHeight: '20px',

            '& i': {
                marginTop: 10,
                left: 15,
                textAlign: 'center',
            },

            '&:after': {
                content: '""',
                position: `absolute`,
                left: 14,
                top: 'calc(-100% / 2)',
                height: '100%',
                width: 0,
                borderLeftWidth: 2,
                borderBottomWidth: 2,
                zIndex: 0,
            },

            '&:first-of-type': {
                '&:after': {
                    height: 0,
                },
            },
        },
    }),
);

const StepperNavigationRoot = styled('nav')(({ theme }) => ({
    color: theme.bino.color.gray,
    textAlign: 'center',
    boxSizing: 'border-box',
    textTransform: 'uppercase',
    marginTop: 40,
    marginBottom: 40,

    '& ul': {
        display: 'flex',
        fontSize: 12,
        justifyContent: 'space-between',
        listStyle: 'none',
        margin: 0,
        padding: 0,
    },
    '@media (max-width: 600px)': {
        marginTop: 10,
        '& ul': {
            display: 'inline-block',
        },
    },
}));

type StepperNavigationProps = {
    data: Array<StepperNavigationItem>;

    /**
     * If `primary`, then we will just make the every item active but will not
     * make the progress bar active.
     * E.g You can see this sample when editing a Project Survey.
     *
     * Else (for `secondary`), will show the progress bar active and
     * use `correct` icon. This is gives more details for being more active.
     * E.g You can see this sample when navigating to CPS intermeidate page.
     * NOTE: For me, this is prop is not that cool but because we can't easily
     * change the UI for CPS intermediate pages through passing some props or className, we gonna opt to this solution. Maybe you have better solution for this, just let me know.
     * */
    variant?: Variant;
    className?: string;
    completed?: boolean;
};

function StepperNavigation({
    data,
    variant = 'primary',
    className,
}: StepperNavigationProps) {
    const activeItemIdx = data.findIndex((item) => !!item.active);
    return (
        <StepperNavigationRoot>
            <ul className={className}>
                {data.map((value, index) => (
                    <StepperNavigationListItem
                        key={index}
                        completed={value.completed}
                        active={activeItemIdx === index}
                        variant={variant}
                        data-content={1 + index}
                    >
                        <i>{value.completed ? <SuccessIcon /> : 1 + index}</i>
                        {value.label}
                    </StepperNavigationListItem>
                ))}
            </ul>
        </StepperNavigationRoot>
    );
}

export default StepperNavigation;
